import type { IReportParams } from '~/modules/report-analytic/utils';

import {
  DEFAULT_SELECTED_COLUMNS_FOR_EXPERIMENT,
  DEFAULT_SELECTED_COLUMNS_FOR_PAGE,
} from '../constants';
import { getParamsByCampaign, getParamsByTime } from '~/modules/report-analytic/utils';
import { useOrderColumnsSelectorStore, useOrderListingStore } from '../stores';
import { EReportParamKeys } from '~/modules/report-analytic/types';
import { useHandleNavigate } from '~/hooks/useHandleNavigate';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { SEARCH_PARAM_KEYS } from '~/constants/navigate';
import { INavigateKey } from '~/types/navigate';
import { ROUTE_PATH } from '~/config/navigate';
import { useLocation } from '@remix-run/react';

export function useNavigateToOrderListing() {
  const isChangeSelectedColumnsPreviously = useOrderColumnsSelectorStore(
    (state) => state.isChangeSelectedColumnsPreviously,
  );
  const setSelectedColumns = useOrderColumnsSelectorStore((state) => state.setSelectedColumns);
  const setPrevLocation = useOrderListingStore((state) => state.setPrevLocation);

  const { handleNavigate } = useHandleNavigate();
  const { currentDates, dateTimePicked } = useMetricsTimeFilter();

  const location = useLocation();
  const pathname = location.pathname;
  const isOnReportPage = pathname.includes(INavigateKey.Reports);

  function getParams(params?: IReportParams) {
    return {
      ...(params?.locationPath && { [EReportParamKeys.PATH]: params.locationPath }),
      ...getParamsByCampaign(params),
      ...getParamsByTime({ ...currentDates, ...params?.customDateInfo }, dateTimePicked),
      ...(params?.mode && { [EReportParamKeys.MODE]: params.mode }),
      ...(params?.from && { [EReportParamKeys.FROM]: params.from }),
      ...(params?.viewScope && { [EReportParamKeys.ORDER_VIEW_SCOPE]: params.viewScope }),
    };
  }

  function handleNavigateToOrderListing(reportParams?: IReportParams, path?: string) {
    const params = getParams(reportParams);
    if (isOnReportPage) {
      setPrevLocation('');
    } else {
      setPrevLocation(getFullPath());
    }

    const isNavigateFromPage = !!params[EReportParamKeys.PATH];

    if (!isChangeSelectedColumnsPreviously) {
      setSelectedColumns(
        isNavigateFromPage
          ? DEFAULT_SELECTED_COLUMNS_FOR_PAGE
          : DEFAULT_SELECTED_COLUMNS_FOR_EXPERIMENT,
      );
    }

    if (isNavigateFromPage) {
      handleNavigate(`${ROUTE_PATH[INavigateKey.OrderListing]}`, { params });
      return;
    }

    handleNavigate(path ?? ROUTE_PATH[INavigateKey.OrderListing], { params });
  }

  return {
    handleNavigateToOrderListing,
  };

  function getFullPath() {
    const search = location.search
      .slice(1)
      .split('&')
      .filter((param) => !SEARCH_PARAM_KEYS.includes(param.split('=')[0]))
      .join('&');

    return `${location.pathname}?${search}`;
  }
}
